import { getPtoStaffingDays, getShiftDisplayDate } from "./dateUtils";
import {
	type Day,
	type Nurse,
	type NurseInShift,
	Preferences,
	type Shift,
	ShiftType,
	type SpecificPreference,
	type TransformedShift,
	type WeekData,
} from "./models";

/**
 * One person's shifts and time off as the calendar's days, from `weeks`: a shift wins its day over time off, and a day
 * they don't work counts the pending swaps aimed at them.
 */
export const transformedShiftsOf = (weeks: WeekData[], nurse: Nurse, dayStart: string, scheduledShiftDeltaMinutes: number): TransformedShift[] => {
	// Create a map to store shifts by date to prevent duplicates
	const shiftsMap = new Map<string, TransformedShift[]>();

	// First, add all days off to the map
	if (nurse.employeeSpecificPreferences) {
		nurse.employeeSpecificPreferences
			.filter((pref: SpecificPreference) => pref.type === Preferences.Avoid)
			.forEach((pref: SpecificPreference) => {
				// The key is the staffing day at midnight, matching how shifts are keyed below — it used to
				// be the PTO's raw instant, so the "don't show time off where there is a shift" guard
				// beneath could never match and never fired.
				for (const day of getPtoStaffingDays(pref.start_date, pref.end_date, dayStart)) {
					const dateKey = day.toISO() as string;
					// Only add time off if there isn't already a shift on this day
					if (!shiftsMap.has(dateKey)) {
						shiftsMap.set(dateKey, [
							{
								date: day,
								hasDayRequest: false,
								hasNightRequest: false,
								isTimeOff: true,
								timeOffType: "Day Off",
								timeOffReason: pref.reason || "",
								isPreferredNot: false,
								isPreferred: false,
								requests: 0,
								departmentId: "",
							},
						]);
					}
				}
			});
	}

	// Then add all shifts from the schedule - these will override any time off
	weeks.forEach((week: WeekData) => {
		week.weeklyShifts.forEach((weeklyShift: Shift) => {
			weeklyShift.days.forEach((day: Day) => {
				const dateKey = day.date.toISO() as string;
				const nurseShifts: NurseInShift[] = day.nurses.filter((n: NurseInShift) => n.nurseId === nurse.id && !n.isPendingToDelete);

				// Count requests for this shift
				let requestCount = 0;

				day.nurses.forEach((n: NurseInShift) => {
					if (n.isPending && !n.isPendingToDelete && n.swapWith === nurse.id) {
						requestCount++;
					}
				});

				if (!nurseShifts.length && !shiftsMap.get(dateKey)) {
					shiftsMap.set(dateKey, [
						{
							date: day.date,
							hasDayRequest: false,
							hasNightRequest: false,
							isTimeOff: false,
							timeOffType: undefined,
							isPreferredNot: false,
							isPreferred: false,
							requests: requestCount,
							departmentId: "",
						},
					]);
				} else {
					nurseShifts.forEach((nurseShift: NurseInShift) => {
						// If there's a shift, it overrides any time off
						// Determine shift type based on time and type
						let shiftType = ShiftType.Day;
						if (
							weeklyShift.type === ShiftType.Night ||
							weeklyShift.type === ShiftType.Evening ||
							nurseShift.startTime.hour >= 18 ||
							nurseShift.startTime.hour < 6
						) {
							shiftType = ShiftType.Night;
						} else if (weeklyShift.type === ShiftType.FullDay) {
							shiftType = ShiftType.All;
						}

						// Before dayStart goes to the previous day, unless it is inside the scheduled grace.
						const adjustedDate = getShiftDisplayDate(nurseShift.startTime, dayStart, scheduledShiftDeltaMinutes);
						const adjustedDateKey = adjustedDate.toISO() as string;

						/**
						 * We should filter out any placeholder shifts (id == null) if there's a real shift on the same day
						 * this may happen if a nurse doesnt have day-time shifts but has night shifts, meaning that we will first create a placeholder and then we will have to discard it!
						 */
						const nurseTransformedShifts: TransformedShift[] = shiftsMap.get(adjustedDateKey)?.filter((transformedShift) => transformedShift.id != null) ?? [];

						shiftsMap.set(adjustedDateKey, [
							...nurseTransformedShifts,
							{
								id: nurseShift.id,
								date: adjustedDate,
								startTime: nurseShift.startTime,
								endTime: nurseShift.endTime,
								location: nurseShift.location || "",
								shiftType,
								isCharge: nurseShift.charge,
								hasDayRequest: false,
								hasNightRequest: false,
								isTimeOff: false, // Never show time off if there's a shift
								timeOffType: undefined,
								timeOffReason: undefined,
								isPreferredNot: false,
								isPreferred: false,
								requests: requestCount,
								bonus: nurseShift.bonus,
								departmentId: nurseShift.departmentId || "",
								notes: nurseShift.notes,
							},
						]);
					});
				}
			});
		});
	});

	// Convert map to array and sort by date
	return Array.from(shiftsMap.values())
		.flat()
		.sort((a: TransformedShift, b: TransformedShift) => a.date.diff(b.date).toMillis());
};
